import { Link } from 'react-router-dom'
import { CharacterClassIcon } from '../../../components'
import { mediaUrl } from '../../../helpers'
import { useTranslation } from 'react-i18next'
import DashboardTile from '../../../components/DashboardTile'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterExternalIconLink from 'app/frontend/base/components/CharacterExternalIconLink'

interface Props {
  character?: wowaudit.Character
}

// TODO: Separate by gender?
const offsetByRace: { [key: string]: { size: string; offset: string } } = {
  Human: { size: 'bg-[length:45rem]', offset: 'bg-[center_top_34%]' },
  Orc: { size: 'bg-[length:45rem]', offset: 'bg-[center_top_36%]' },
  Dwarf: { size: 'bg-[length:45rem]', offset: 'bg-[center_top_41%]' },
  'Night Elf': { size: 'bg-[length:45rem]', offset: 'bg-[center_top_27%]' },
  Undead: { size: 'bg-[length:45rem]', offset: 'bg-[center_top_41%]' },
  Tauren: { size: 'bg-[length:35rem]', offset: 'bg-[center_top_30%]' },
  Gnome: { size: 'bg-[length:45rem]', offset: 'bg-[center_top_55%]' },
  Troll: { size: 'bg-[length:45rem]', offset: 'bg-[center_top_35%]' },
  Goblin: { size: 'bg-[length:45rem]', offset: 'bg-[center_top_49%]' },
  'Blood Elf': { size: 'bg-[length:45rem]', offset: 'bg-[center_top_39%]' },
  Draenei: { size: 'bg-[length:45rem]', offset: 'bg-[center_top_28%]' },
  Worgen: { size: 'bg-[length:45rem]', offset: 'bg-[center_top_36%]' },
  'Pandaren N': { size: 'bg-[length:45rem]', offset: 'bg-[center_top_29%]' },
  'Pandaren A': { size: 'bg-[length:45rem]', offset: 'bg-[center_top_29%]' },
  'Pandaren H': { size: 'bg-[length:45rem]', offset: 'bg-[center_top_29%]' },
  Nightborne: { size: 'bg-[length:45rem]', offset: 'bg-[center_top_34%]' },
  'Highmountain Tauren': {
    size: 'bg-[length:30rem]',
    offset: 'bg-[center_top_22%]',
  },
  'Void Elf': { size: 'bg-[length:45rem]', offset: 'bg-[center_top_39%]' },
  'Lightforged Draenei': {
    size: 'bg-[length:45rem]',
    offset: 'bg-[center_top_30%]',
  },
  'Zandalari Troll': {
    size: 'bg-[length:45rem]',
    offset: 'bg-[center_top_24%]',
  },
  'Kul Tiran': { size: 'bg-[length:45rem]', offset: 'bg-[center_top_24%]' },
  'Dark Iron Dwarf': {
    size: 'bg-[length:45rem]',
    offset: 'bg-[center_top_41%]',
  },
  Vulpera: { size: 'bg-[length:45rem]', offset: 'bg-[center_top_46%]' },
  "Mag'har Orc": { size: 'bg-[length:45rem]', offset: 'bg-[center_top_36%]' },
  Mechagnome: { size: 'bg-[length:45rem]', offset: 'bg-[center_top_52%]' },
  'Dracthyr A': { size: 'bg-[length:45rem]', offset: 'bg-[center_top_27%]' },
  'Dracthyr H': { size: 'bg-[length:45rem]', offset: 'bg-[center_top_27%]' },
  '': { size: 'bg-[length:45rem]', offset: 'bg-[center_top_50%]' },
}

interface Props {
  character?: wowaudit.Character
  characterReference?: wowaudit.CharacterReference
  uriRegion: string
  uriRealm: string
  uriName: string
  isPending: boolean
}

const CharacterHeader = ({
  character,
  characterReference,
  uriRegion,
  uriRealm,
  uriName,
  isPending,
}: Props) => {
  const characterInfo = character || characterReference
  const { t } = useTranslation()
  const offset =
    offsetByRace[
      characterInfo?.mediaZone && characterInfo.race
        ? characterInfo.race
        : 'Human'
    ]

  return (
    <header className='border-b-2 ml-[-2rem] mr-[-2rem] max-sm:ml-[-1rem] max-sm:mr-[-1rem]'>
      <div className='flex items-center mt-[-2rem] ml-[2rem] mr-[2rem] max-sm:ml-[1rem] max-sm:mr-[1rem]'>
        <figure
          className={`hidden sm:block w-[14rem] h-[7rem] ${offset?.size} ${offset?.offset} scale-x-[-1]`}
          style={{
            backgroundImage: `url(${mediaUrl(characterInfo, 'main-raw')})`,
          }}
        ></figure>

        <div className='grow flex flex-col my-2'>
          <span
            className={`text-sm text-muted-foreground capitalize h-[1.6rem] leading-6`}
          >
            {characterInfo?.realm?.nameWithRegion ||
              `${uriRealm} (${uriRegion.toUpperCase()})`}
          </span>

          <h1 className='leading-none max-sm:text-xl'>
            <span className='capitalize'>
              {characterInfo?.name || decodeURIComponent(uriName).slice(0, 12)}
            </span>
            {character?.guildReference ? (
              <Link
                className={`text-sm ml-4 ${
                  character.guildReference.faction === 'Alliance'
                    ? 'text-faction-alliance'
                    : 'text-faction-horde'
                }`}
                to={character.guildReference.defaultPath}
              >
                &lt;{character.guildReference.name}&gt;
              </Link>
            ) : null}
          </h1>
          <span
            className={`flex items-center gap-1 text-sm mt-2 ${
              isPending ? 'opacity-0' : 'opacity-100'
            } transition-opacity`}
          >
            {characterInfo?.currentSpec ? (
              <BlizzardIcon iconName={characterInfo.currentSpec?.icon} />
            ) : (
              <CharacterClassIcon
                characterClass={characterInfo?.characterClass}
              />
            )}

            <strong
              className={`ml-1 min-w-[1.2rem] duration-300 ${
                characterInfo?.level ? 'opacity-100' : 'opacity-0'
              } transition-opacity`}
            >
              {characterInfo?.level}
            </strong>
            <span className={`ml-0.5`}>
              {characterInfo?.currentSpec
                ? `${characterInfo.currentSpec.name} ${characterInfo.characterClass}`
                : characterInfo?.characterClass || t('shared.unknown')}
            </span>
          </span>
        </div>

        <div className='flex'>
          <DashboardTile
            className='hidden lg:block'
            innerClassName='h-[2rem]'
            title='achievements'
            dataPresent={!!character?.details}
          >
            <div>{character?.details?.achievementPoints || '-'}</div>
          </DashboardTile>

          <DashboardTile
            className='hidden md:block'
            innerClassName='h-[2rem]'
            title='m+ score'
            dataPresent={!!character?.details}
          >
            <div
              style={{
                color:
                  character?.details?.mythicRatingColor?.overall || 'inherit',
              }}
            >
              {character?.details?.mythicRating || '-'}
            </div>
          </DashboardTile>

          <DashboardTile
            innerClassName='h-[2rem]'
            title='links'
            dataPresent={true}
          >
            <div className='flex gap-2'>
              <CharacterExternalIconLink
                type='blizzard'
                backendUrl={characterInfo?.blizzardUrl}
                realmName={uriRealm}
                region={uriRegion}
                entityName={uriName}
              />
              <CharacterExternalIconLink
                type='wcl'
                backendUrl={characterInfo?.wclUrl}
                realmName={uriRealm}
                region={uriRegion}
                entityName={uriName}
              />
              <CharacterExternalIconLink
                type='raiderio'
                backendUrl={characterInfo?.raiderioUrl}
                realmName={uriRealm}
                region={uriRegion}
                entityName={uriName}
              />
            </div>
          </DashboardTile>
        </div>
      </div>
    </header>
  )
}

export default CharacterHeader
